/**
 * Predictive & intelligence section (Analytics spec §3.4.2 transparent churn
 * score, §3.4.5 RFM, §4.6 business-health composite, §3.1 renewal forecast,
 * §3.7 retail attach). Reads the 0148 P2 views + the reused v_renewal_forecast
 * (0065). The churn score is deliberately transparent — every flagged member
 * shows the reasons behind the number. Owner/manager (route-guarded). Backend
 * data, surfaced read-only; clear empty states where the data isn't there yet.
 */
import {
  getBusinessHealth,
  getChurnRisk,
  getLtvByPlan,
  getRenewalForecast,
  getRetailAttach,
  getRfmSegments,
} from '@gym/api-client';
import { useQuery } from '@tanstack/react-query';
import { Activity, CalendarClock, HeartPulse, ShoppingBasket, TrendingUp } from 'lucide-react';
import { type ReactNode, useMemo } from 'react';
import { useTranslation } from 'react-i18next';

import { RankedBarChart } from '../../components/charts/index.js';
import { ExpandableCard } from '../../components/ui/ExpandableCard.js';
import { EmptyState } from '../../components/ui/Feedback.js';
import { KpiCard } from '../../components/ui/KpiCard.js';
import { ChartCard } from '../../components/ui/Layout.js';
import { Money, useFormatters, useLocale } from '../../components/ui/Money.js';
import { Chip } from '../../components/ui/StatusPill.js';
import { Table, TableWrap, TD, TH, THead, TR } from '../../components/ui/Table.js';
import { useAuth } from '../../lib/auth.js';
import { supabase } from '../../lib/supabase.js';

const SEGMENT_COLOR: Record<string, string> = {
  champions: '#22C55E',
  loyal: '#38BDF8',
  new: '#A78BFA',
  at_risk: '#F59E0B',
  hibernating: '#64748B',
};

function healthTone(score: number | null | undefined): 'success' | 'warning' | 'danger' | 'info' {
  if (score == null) return 'info';
  return score >= 75 ? 'success' : score >= 50 ? 'warning' : 'danger';
}

function riskTone(score: number): 'success' | 'warning' | 'danger' {
  return score >= 60 ? 'danger' : score >= 30 ? 'warning' : 'success';
}

export function IntelligenceAnalytics(): ReactNode {
  const { t } = useTranslation();
  const fmt = useFormatters();
  const locale = useLocale();
  const { claims } = useAuth();
  const tenantId = claims?.tenant_id ?? '';
  const on = { enabled: !!tenantId };

  const health = useQuery({
    queryKey: ['an_health', tenantId],
    queryFn: () => getBusinessHealth(supabase, tenantId),
    ...on,
  });
  const risk = useQuery({
    queryKey: ['an_risk', tenantId],
    queryFn: () => getChurnRisk(supabase, tenantId),
    ...on,
  });
  const rfm = useQuery({
    queryKey: ['an_rfm', tenantId],
    queryFn: () => getRfmSegments(supabase, tenantId),
    ...on,
  });
  const forecast = useQuery({
    queryKey: ['an_forecast', tenantId],
    queryFn: () => getRenewalForecast(supabase, tenantId),
    ...on,
  });
  const attach = useQuery({
    queryKey: ['an_attach', tenantId],
    queryFn: () => getRetailAttach(supabase, tenantId),
    ...on,
  });
  // LTV is also surfaced in the Plans tab; here it feeds the headline best-LTV plan.
  const ltv = useQuery({
    queryKey: ['an_ltv', tenantId],
    queryFn: () => getLtvByPlan(supabase, tenantId),
    ...on,
  });

  const driverBars = useMemo(() => {
    const h = health.data;
    if (!h) return [];
    const rows: { label: string; value: number; color: string }[] = [];
    const push = (label: string, v: number | null, color: string): void => {
      if (v != null) rows.push({ label, value: v, color });
    };
    push(t('analytics.intel.retentionScore'), h.retention_score, '#22C55E');
    push(t('analytics.intel.engagementScore'), h.engagement_score, '#38BDF8');
    push(t('analytics.intel.growthScore'), h.growth_score, '#A78BFA');
    push(t('analytics.intel.marginScore'), h.margin_score, '#F97316');
    return rows;
  }, [health.data, t]);

  const segmentBars = useMemo(
    () =>
      (rfm.data ?? []).map((s) => ({
        label: t(`analytics.segment.${s.segment}`, { defaultValue: s.segment }),
        value: s.members,
        color: SEGMENT_COLOR[s.segment] ?? '#94A3B8',
      })),
    [rfm.data, t],
  );

  const forecastTotals = useMemo(() => {
    const rows = forecast.data ?? [];
    return {
      due: rows.reduce((s, r) => s + r.memberships_due, 0),
      revenue: rows.reduce((s, r) => s + r.projected_revenue, 0),
    };
  }, [forecast.data]);

  const bestLtv = useMemo(() => {
    const rows = (ltv.data ?? []).filter((r) => r.ltv != null);
    return rows.length ? rows[0] : null;
  }, [ltv.data]);

  function reasonsFor(r: NonNullable<typeof risk.data>[number]): string[] {
    const out: string[] = [];
    if (r.reason_inactive) out.push(t('analytics.reason.inactive'));
    if (r.reason_no_recent_visits) out.push(t('analytics.reason.noRecentVisits'));
    if (r.reason_new_member) out.push(t('analytics.reason.newMember'));
    if (r.reason_frozen_before) out.push(t('analytics.reason.frozenBefore'));
    if (r.reason_payment_failed) out.push(t('analytics.reason.paymentFailed'));
    return out;
  }

  return (
    <div className="grid gap-5">
      {/* ── Business-health hero + KPI row ─────────────────────────────────── */}
      <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 xl:grid-cols-4">
        <KpiCard
          label={t('analytics.intel.businessHealth')}
          value={health.data?.health_score != null ? fmt.number(health.data.health_score) : '—'}
          icon={HeartPulse}
          tone={healthTone(health.data?.health_score)}
          loading={health.isLoading}
          hint={t('analytics.intel.healthHint')}
        />
        <KpiCard
          label={t('analytics.intel.membershipsDue')}
          value={fmt.number(forecastTotals.due)}
          icon={CalendarClock}
          tone="info"
          loading={forecast.isLoading}
          hint={t('analytics.intel.renewalSubtitle')}
        />
        <KpiCard
          label={t('analytics.intel.attachRate')}
          value={attach.data?.attach_pct != null ? `${attach.data.attach_pct}%` : '—'}
          icon={ShoppingBasket}
          tone="primary"
          loading={attach.isLoading}
          hint={t('analytics.intel.attachHint')}
        />
        <KpiCard
          label={t('analytics.intel.ltv')}
          value={bestLtv ? bestLtv.plan_name : '—'}
          icon={TrendingUp}
          tone="success"
          loading={ltv.isLoading}
          hint={
            bestLtv?.ltv != null
              ? t('analytics.intel.ltvBestHint', { v: fmt.money(bestLtv.ltv) })
              : undefined
          }
        />
      </div>

      <div className="grid grid-cols-1 gap-4 xl:grid-cols-2">
        <ChartCard
          title={t('analytics.intel.businessHealth')}
          subtitle={t('analytics.intel.healthDrivers')}
          loading={health.isLoading}
          empty={driverBars.length === 0 ? t('common.noData') : undefined}
        >
          <RankedBarChart data={driverBars} formatValue={(v) => `${v}`} />
        </ChartCard>
        <ChartCard
          title={t('analytics.intel.rfm')}
          subtitle={t('analytics.intel.rfmSubtitle')}
          loading={rfm.isLoading}
          empty={segmentBars.length === 0 ? t('common.noData') : undefined}
        >
          <RankedBarChart
            data={segmentBars}
            formatValue={(v) => new Intl.NumberFormat(locale).format(v)}
          />
        </ChartCard>
      </div>

      {/* ── Predictive churn risk (transparent) ───────────────────────────── */}
      <ExpandableCard
        title={t('analytics.intel.churnRisk')}
        subtitle={t('analytics.intel.churnRiskSubtitle')}
        loading={risk.isLoading}
        empty={(risk.data ?? []).length === 0 ? t('analytics.intel.noRisk') : undefined}
      >
        <TableWrap>
          <Table>
            <THead>
              <tr>
                <TH>{t('members.fields.firstName')}</TH>
                <TH>{t('members.fields.phone')}</TH>
                <TH numeric>{t('analytics.intel.riskScore')}</TH>
                <TH>{t('analytics.intel.reasonsLabel')}</TH>
                <TH numeric>{t('analytics.intel.mrrAtRisk')}</TH>
              </tr>
            </THead>
            <tbody>
              {(risk.data ?? []).slice(0, 50).map((r) => (
                <TR key={r.member_id}>
                  <TD className="font-medium">
                    {r.first_name} {r.last_name}
                  </TD>
                  <TD className="text-muted">{r.phone ?? '—'}</TD>
                  <TD numeric>
                    <Chip tone={riskTone(r.risk_score)}>{r.risk_score}</Chip>
                  </TD>
                  <TD>
                    <div className="flex flex-wrap gap-1">
                      {reasonsFor(r).map((reason) => (
                        <Chip key={reason} tone="neutral">
                          {reason}
                        </Chip>
                      ))}
                    </div>
                  </TD>
                  <TD numeric>
                    {r.monthly_value != null ? <Money minor={r.monthly_value} /> : '—'}
                  </TD>
                </TR>
              ))}
            </tbody>
          </Table>
        </TableWrap>
      </ExpandableCard>

      {/* ── Renewal forecast (reused v_renewal_forecast) ──────────────────── */}
      <ExpandableCard
        title={t('analytics.intel.renewalForecast')}
        subtitle={t('analytics.intel.renewalSubtitle')}
        loading={forecast.isLoading}
        empty={(forecast.data ?? []).length === 0 ? t('common.noData') : undefined}
      >
        {(forecast.data ?? []).length === 0 ? (
          <EmptyState icon={Activity} title={t('common.noData')} />
        ) : (
          <TableWrap>
            <Table>
              <THead>
                <tr>
                  <TH>{t('analytics.intel.renewalMonth')}</TH>
                  <TH>{t('memberships.planType')}</TH>
                  <TH numeric>{t('analytics.intel.membershipsDue')}</TH>
                  <TH numeric>{t('analytics.intel.projectedRevenue')}</TH>
                </tr>
              </THead>
              <tbody>
                {(forecast.data ?? []).map((r) => (
                  <TR key={`${r.renewal_month}-${r.plan_id}`}>
                    <TD className="font-medium">
                      {fmt.date(r.renewal_month, { month: 'short', year: 'numeric' })}
                    </TD>
                    <TD>{t(`plans.type.${r.plan_type}`, { defaultValue: r.plan_type })}</TD>
                    <TD numeric>{fmt.number(r.memberships_due)}</TD>
                    <TD numeric>
                      <Money minor={r.projected_revenue} />
                    </TD>
                  </TR>
                ))}
              </tbody>
            </Table>
          </TableWrap>
        )}
      </ExpandableCard>
    </div>
  );
}
